import React, { useState, useRef } from 'react';
import './BuildingParametersPanel.css';

const BuildingParametersPanel = ({ 
  buildingParams, 
  onClose, 
  onGenerate3D, 
  generationStatus 
}) => {
  const [params, setParams] = useState(buildingParams);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState('parameters');
  const fileInputRef = useRef(null);

  const handleParamChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddressChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [key]: value
      }
    }));
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedPhotos(prev => [...prev, {
            id: Date.now() + Math.random(),
            file: file,
            url: e.target.result,
            name: file.name,
            type: 'facade' // можно будет менять на roof, interior и т.д.
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleRemovePhoto = (photoId) => {
    setUploadedPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  const handlePhotoTypeChange = (photoId, type) => {
    setUploadedPhotos(prev => prev.map(photo => 
      photo.id === photoId ? { ...photo, type } : photo
    ));
  };

  const calculateCompletionPercentage = () => {
    let completed = 0;
    let total = 8;

    if (params.building_type && params.building_type !== 'unknown') completed++;
    if (params.height || params.levels) completed++;
    if (params.geometry) completed++;
    if (params.base_area) completed++;
    if (params.address.housenumber || params.name) completed++;
    if (params.roof_shape && params.roof_shape !== 'unknown') completed++;
    if (params.wall_material && params.wall_material !== 'unknown') completed++;
    if (uploadedPhotos.length > 0) completed++;

    return Math.round((completed / total) * 100);
  };

  const canGenerate = () => {
    return (
      params.geometry && 
      (params.height || params.levels) && 
      uploadedPhotos.length > 0 &&
      generationStatus !== 'generating'
    );
  };

  const handleGenerate = () => {
    const finalParams = {
      ...params,
      photos: uploadedPhotos,
      completion_percentage: calculateCompletionPercentage()
    };
    
    onGenerate3D(finalParams);
  };

  const estimatedHeight = params.height || (params.levels ? params.levels * 3.5 : 0);
  const completionPercentage = calculateCompletionPercentage();

  return (
    <div className="building-parameters-panel">
      <div className="panel-overlay" onClick={onClose}></div>
      
      <div className="panel-content">
        {/* Header */}
        <div className="panel-header">
          <div className="panel-title">
            <h3>🏗️ 3D Building Generator</h3>
            <div className="completion-indicator">
              <div className="completion-bar">
                <div 
                  className="completion-fill" 
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
              <span className="completion-text">{completionPercentage}% Complete</span>
            </div>
          </div>
          <button className="panel-close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="panel-tabs">
          <button 
            className={`tab ${activeTab === 'parameters' ? 'active' : ''}`}
            onClick={() => setActiveTab('parameters')}
          >
            📋 Parameters
          </button>
          <button 
            className={`tab ${activeTab === 'photos' ? 'active' : ''}`}
            onClick={() => setActiveTab('photos')}
          >
            📸 Photos ({uploadedPhotos.length})
          </button>
          <button 
            className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            👁️ Preview
          </button>
        </div>

        {/* Tab Content */}
        <div className="panel-body">
          
          {/* Parameters Tab */}
          {activeTab === 'parameters' && (
            <div className="parameters-tab">
              
              {/* Basic Info */}
              <div className="param-section">
                <h4>Basic Information</h4>
                <div className="param-grid">
                  <div className="param-item">
                    <label>Building Name</label>
                    <input
                      type="text"
                      value={params.name || ''}
                      onChange={(e) => handleParamChange('name', e.target.value)}
                      placeholder="Enter building name"
                    />
                  </div>
                  
                  <div className="param-item">
                    <label>Building Type</label>
                    <select
                      value={params.building_type || 'unknown'}
                      onChange={(e) => handleParamChange('building_type', e.target.value)}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="office">Office</option>
                      <option value="industrial">Industrial</option>
                      <option value="retail">Retail</option>
                      <option value="hotel">Hotel</option>
                      <option value="hospital">Hospital</option>
                      <option value="school">School</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Dimensions */}
              <div className="param-section">
                <h4>Dimensions</h4>
                <div className="param-grid">
                  <div className="param-item">
                    <label>Height (meters)</label>
                    <input
                      type="number"
                      value={params.height || ''}
                      onChange={(e) => handleParamChange('height', parseFloat(e.target.value))}
                      placeholder="Building height"
                      step="0.1"
                    />
                  </div>
                  
                  <div className="param-item">
                    <label>Number of Levels</label>
                    <input
                      type="number"
                      value={params.levels || ''}
                      onChange={(e) => handleParamChange('levels', parseInt(e.target.value))}
                      placeholder="Number of floors"
                    />
                    {params.levels && (
                      <small>≈ {params.levels * 3.5}m height</small>
                    )}
                  </div>

                  <div className="param-item">
                    <label>Base Area (m²)</label>
                    <input
                      type="number"
                      value={params.base_area || ''}
                      onChange={(e) => handleParamChange('base_area', parseFloat(e.target.value))}
                      placeholder="Base area"
                      step="0.1"
                    />
                  </div>

                  <div className="param-item">
                    <label>Min Height (meters)</label>
                    <input
                      type="number"
                      value={params.min_height || 0}
                      onChange={(e) => handleParamChange('min_height', parseFloat(e.target.value))}
                      placeholder="Minimum height"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="param-section">
                <h4>Address</h4>
                <div className="param-grid">
                  <div className="param-item">
                    <label>House Number</label>
                    <input
                      type="text"
                      value={params.address.housenumber || ''}
                      onChange={(e) => handleAddressChange('housenumber', e.target.value)}
                      placeholder="House number"
                    />
                  </div>
                  
                  <div className="param-item">
                    <label>Street</label>
                    <input
                      type="text"
                      value={params.address.street || ''}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      placeholder="Street name"
                    />
                  </div>

                  <div className="param-item">
                    <label>City</label>
                    <input
                      type="text"
                      value={params.address.city || ''}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      placeholder="City"
                    />
                  </div>

                  <div className="param-item">
                    <label>Postcode</label>
                    <input
                      type="text"
                      value={params.address.postcode || ''}
                      onChange={(e) => handleAddressChange('postcode', e.target.value)}
                      placeholder="Postcode"
                    />
                  </div>
                </div>
              </div>

              {/* Materials & Style */}
              <div className="param-section">
                <h4>Materials & Style</h4>
                <div className="param-grid">
                  <div className="param-item">
                    <label>Roof Shape</label>
                    <select
                      value={params.roof_shape || 'flat'}
                      onChange={(e) => handleParamChange('roof_shape', e.target.value)}
                    >
                      <option value="flat">Flat</option>
                      <option value="gabled">Gabled</option>
                      <option value="hipped">Hipped</option>
                      <option value="pyramidal">Pyramidal</option>
                      <option value="mansard">Mansard</option>
                    </select>
                  </div>
                  
                  <div className="param-item">
                    <label>Wall Material</label>
                    <select
                      value={params.wall_material || 'unknown'}
                      onChange={(e) => handleParamChange('wall_material', e.target.value)}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="brick">Brick</option>
                      <option value="concrete">Concrete</option>
                      <option value="wood">Wood</option>
                      <option value="glass">Glass</option>
                      <option value="stone">Stone</option>
                      <option value="metal">Metal</option>
                    </select>
                  </div>

                  <div className="param-item">
                    <label>Roof Material</label>
                    <select
                      value={params.roof_material || 'unknown'}
                      onChange={(e) => handleParamChange('roof_material', e.target.value)}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="tile">Tile</option>
                      <option value="metal">Metal</option>
                      <option value="concrete">Concrete</option>
                      <option value="asphalt">Asphalt</option>
                      <option value="slate">Slate</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Data Status */}
              <div className="param-section">
                <h4>Data Status</h4>
                <div className="status-grid">
                  <div className={`status-item ${params.completion_status.has_geometry ? 'complete' : 'incomplete'}`}>
                    <span className="status-icon">{params.completion_status.has_geometry ? '✅' : '❌'}</span>
                    <span>Building Geometry</span>
                  </div>
                  <div className={`status-item ${params.completion_status.has_height ? 'complete' : 'incomplete'}`}>
                    <span className="status-icon">{params.completion_status.has_height ? '✅' : '❌'}</span>
                    <span>Height Data</span>
                  </div>
                  <div className={`status-item ${params.completion_status.has_address ? 'complete' : 'incomplete'}`}>
                    <span className="status-icon">{params.completion_status.has_address ? '✅' : '❌'}</span>
                    <span>Address Info</span>
                  </div>
                  <div className={`status-item ${params.completion_status.has_building_type ? 'complete' : 'incomplete'}`}>
                    <span className="status-icon">{params.completion_status.has_building_type ? '✅' : '❌'}</span>
                    <span>Building Type</span>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="photos-tab">
              
              <div className="upload-section">
                <h4>Upload Building Photos</h4>
                <p>Upload photos of the building for texture generation and reference</p>
                
                <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
                  <div className="upload-content">
                    <span className="upload-icon">📷</span>
                    <span>Click to upload photos</span>
                    <small>Supports JPG, PNG, WebP</small>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                </div>
              </div>

              {uploadedPhotos.length > 0 && (
                <div className="photos-grid">
                  {uploadedPhotos.map(photo => (
                    <div key={photo.id} className="photo-item">
                      <img src={photo.url} alt={photo.name} />
                      <div className="photo-controls">
                        <select
                          value={photo.type}
                          onChange={(e) => handlePhotoTypeChange(photo.id, e.target.value)}
                        >
                          <option value="facade">Facade</option>
                          <option value="roof">Roof</option>
                          <option value="entrance">Entrance</option>
                          <option value="detail">Detail</option>
                          <option value="context">Context</option>
                        </select>
                        <button 
                          className="remove-photo"
                          onClick={() => handleRemovePhoto(photo.id)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="photo-name">{photo.name}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="photo-tips">
                <h5>📸 Photo Tips for Better Results:</h5>
                <ul>
                  <li><strong>Facade:</strong> Full front view of the building</li>
                  <li><strong>Roof:</strong> Aerial or elevated view of the roof</li>
                  <li><strong>Entrance:</strong> Main entrance and doors</li>
                  <li><strong>Detail:</strong> Close-up textures and materials</li>
                  <li><strong>Context:</strong> Building in surrounding environment</li>
                </ul>
              </div>

            </div>
          )}

          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <div className="preview-tab">
              
              <div className="preview-summary">
                <h4>Generation Summary</h4>
                
                <div className="summary-grid">
                  <div className="summary-item">
                    <label>Building Name:</label>
                    <span>{params.name || 'Unnamed Building'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Type:</label>
                    <span>{params.building_type || 'Unknown'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Estimated Height:</label>
                    <span>{estimatedHeight.toFixed(1)}m</span>
                  </div>
                  <div className="summary-item">
                    <label>Base Area:</label>
                    <span>{params.base_area ? `${params.base_area.toFixed(1)}m²` : 'Auto-calculated'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Photos:</label>
                    <span>{uploadedPhotos.length} uploaded</span>
                  </div>
                  <div className="summary-item">
                    <label>Completion:</label>
                    <span>{completionPercentage}%</span>
                  </div>
                </div>
              </div>

              <div className="generation-options">
                <h4>3D Generation Options</h4>
                
                <div className="option-grid">
                  <div className="option-item">
                    <label>
                      <input type="checkbox" defaultChecked />
                      Generate textures from photos
                    </label>
                  </div>
                  <div className="option-item">
                    <label>
                      <input type="checkbox" defaultChecked />
                      Include interior details
                    </label>
                  </div>
                  <div className="option-item">
                    <label>
                      <input type="checkbox" defaultChecked />
                      Optimize for web viewing
                    </label>
                  </div>
                  <div className="option-item">
                    <label>
                      <input type="checkbox" />
                      High-resolution output
                    </label>
                  </div>
                </div>
              </div>

              {!canGenerate() && (
                <div className="generation-requirements">
                  <h5>⚠️ Requirements for 3D Generation:</h5>
                  <ul>
                    {!params.geometry && <li>Building geometry is required</li>}
                    {!(params.height || params.levels) && <li>Height or number of levels is required</li>}
                    {uploadedPhotos.length === 0 && <li>At least one photo is required</li>}
                  </ul>
                </div>
              )}

            </div>
          )}

        </div>

        {/* Footer */}
        <div className="panel-footer">
          <div className="footer-info">
            <span>Coordinates: {params.coordinates?.lat.toFixed(6)}, {params.coordinates?.lng.toFixed(6)}</span>
            {params.osm_id && <span>OSM ID: {params.osm_id}</span>}
          </div>
          
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className={`btn-primary ${canGenerate() ? '' : 'disabled'}`}
              onClick={handleGenerate}
              disabled={!canGenerate() || generationStatus === 'generating'}
            >
              {generationStatus === 'generating' ? (
                <>
                  <span className="spinner"></span>
                  Generating...
                </>
              ) : (
                '🏗️ Generate 3D Model'
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BuildingParametersPanel;